/**
 * Parse a trimmed string from a `<TextField type="number">` for a percent input
 * (0–100 inclusive). Returns a discriminated union so callers can distinguish a
 * deliberate clear (`{ ok: true, value: null }` for `''`) from a valid value
 * from an invalid one — without ever letting `Number('abc') → NaN` slip into a
 * PATCH payload as a silent `null`.
 *
 * `allowEmpty: false` (default for required inputs) rejects an empty string;
 * `allowEmpty: true` (the rate-override case) accepts it and yields `null` so
 * the caller can clear the override.
 */
export type ParsePercentResult =
  | { ok: true; value: number | null }
  | { ok: false; reason: 'empty' | 'not-finite' | 'out-of-range' }

export const parsePercent = (
  raw: string,
  { allowEmpty }: { allowEmpty: boolean },
): ParsePercentResult => {
  const trimmed = raw.trim()

  if (trimmed === '') {
    if (allowEmpty) {
      return { ok: true, value: null }
    }

    return { ok: false, reason: 'empty' }
  }

  const n = Number(trimmed)

  if (!Number.isFinite(n)) {
    return { ok: false, reason: 'not-finite' }
  }
  if (n < 0 || n > 100) {
    return { ok: false, reason: 'out-of-range' }
  }

  return { ok: true, value: n }
}
